// Generated by TaroBase SDK Generator: 2026-08-23T03:59:35.512Z
// Collection: userAvatars/$walletAddress
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * User avatar image URL keyed by wallet address. Separate from userProfiles so users can change avatars freely without touching the username cooldown. Deletable by the owner or by the backend (PROJECT_VAULT_ADDRESS) as part of creator-profile teardown.
 */

export interface UserAvatarsRequest {
  avatarUrl: string;
  updatedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface UserAvatarsResponse {
  avatarUrl: string;
  updatedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a UserAvatars operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUserAvatars(walletAddress: string, data: UserAvatarsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `userAvatars/${walletAddress}`, document: doc };
}

/**
 * User creates their own avatar record (document ID must equal their wallet address). Caller must equal $walletAddress. avatarUrl should be the URL returned from Poof set_file upload. updatedAt should be set to current Unix seconds. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUserAvatars(walletAddress: string, data: UserAvatarsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`userAvatars/${walletAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting UserAvatars: ${formatError(error)}`);
    return false;
  }
}

export type UserAvatarsRequestUpdate = Partial<UserAvatarsRequest>;

/**
 * Build a UserAvatars update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateUserAvatars(walletAddress: string, data: UserAvatarsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `userAvatars/${walletAddress}`, document: doc };
}

/**
 * Only the wallet owner can update their own avatar. Used when the user uploads a new avatar image (re-upload via set_file, then write the new URL and Unix-seconds updatedAt). (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateUserAvatars(walletAddress: string, data: UserAvatarsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`userAvatars/${walletAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating UserAvatars: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read any user's avatar record (avatars are public).
   (Get Single Item)
 */
export async function getUserAvatars(walletAddress: string): Promise<UserAvatarsResponse | null> {
  try {
    const result = await get(`userAvatars/${walletAddress}`);
    return result as UserAvatarsResponse | null;
  } catch (error) {
    console.error(`Error getting UserAvatars: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single UserAvatars document. (
  Read Operation Details: Anyone can read any user's avatar record (avatars are public).
  )
 */
export function subscribeUserAvatars(callback: (data: UserAvatarsResponse | null) => void, walletAddress: string): Promise<() => Promise<void>> {
  return subscribe(`userAvatars/${walletAddress}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UserAvatarsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to UserAvatars at path '${`userAvatars/${walletAddress}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many UserAvatars items from collection userAvatars
 
  Read Operation Details: Anyone can read any user's avatar record (avatars are public).
  
 */
export async function getManyUserAvatars(filter: string = ""): Promise<UserAvatarsResponse[]> {
  try {
    const results = await get("userAvatars", { prompt: filter });
    return Array.isArray(results) ? results as UserAvatarsResponse[] : [];
  } catch (error) {
    console.error(`Error getting UserAvatars collection at path 'userAvatars': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UserAvatars collection at userAvatars
 
  Read Operation Details: Anyone can read any user's avatar record (avatars are public).
  
 */
export function subscribeManyUserAvatars(callback: (data: UserAvatarsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("userAvatars", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UserAvatarsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UserAvatars collection at path '${"userAvatars"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all UserAvatars items from collection userAvatars
 
  Read Operation Details: Anyone can read any user's avatar record (avatars are public).
  
 */
export async function getAllUserAvatars(filter: string = ""): Promise<UserAvatarsResponse[]> {
  try {
    const results = await get("userAvatars", { prompt: filter });
    return Array.isArray(results) ? results as UserAvatarsResponse[] : [];
  } catch (error) {
    console.error(`Error getting UserAvatars collection at path 'userAvatars': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UserAvatars collection at userAvatars
 
  Read Operation Details: Anyone can read any user's avatar record (avatars are public).
  
 */
export function subscribeAllUserAvatars(callback: (data: UserAvatarsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("userAvatars", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UserAvatarsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UserAvatars collection at path '${"userAvatars"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count UserAvatars items in collection userAvatars.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countUserAvatars(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("userAvatars", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting UserAvatars at path 'userAvatars': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on UserAvatars items in collection userAvatars.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateUserAvatars(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("userAvatars", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating UserAvatars at path 'userAvatars': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Allowed for the owner ($walletAddress == @user.address) or the backend vault (PROJECT_VAULT_ADDRESS). Frontend should NOT call delete directly — avatar deletion is performed by the backend DELETE /api/profile route as part of the atomic creator-profile teardown. The owner branch is permitted defensively.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteUserAvatars(walletAddress: string): Promise<boolean> {
  try {
    const succeeded = await set(`userAvatars/${walletAddress}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting UserAvatars: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for UserAvatars for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteUserAvatars(walletAddress: string): DocumentOperation {
  return { path: `userAvatars/${walletAddress}`, document: null };
}

